import {
    DEFAULT_MERGE_FACE_TIMELINE_OPTIONS,
    filterFacesArrayByMinEdge,
    facesSimilarForMerge,
} from '../mergeFaceTimeline.js';

/** @typedef {{ x: number, y: number, width: number, height: number, isSubject?: boolean }} FaceData */
/** @typedef {{ currentTime: number, facesData: FaceData[] }} FragmentSampleRow */

export const DEFAULT_FRAGMENT_CONSENSUS_OPTS = {
    minFaceWidth: DEFAULT_MERGE_FACE_TIMELINE_OPTIONS.minFaceWidth,
    minFaceHeight: DEFAULT_MERGE_FACE_TIMELINE_OPTIONS.minFaceHeight,
    faceAreaChangeRatioMax: DEFAULT_MERGE_FACE_TIMELINE_OPTIONS.faceAreaChangeRatioMax,
    faceBoxIouMin: 0.5,
    faceCenterOffsetHalfDiagonal: DEFAULT_MERGE_FACE_TIMELINE_OPTIONS.faceCenterOffsetHalfDiagonal,
};

const mergeSimilarityOpts = opts => ({
    faceAreaChangeRatioMax: opts.faceAreaChangeRatioMax,
    faceBoxIouMin: opts.faceBoxIouMin,
    faceCenterOffsetHalfDiagonal: opts.faceCenterOffsetHalfDiagonal,
});

function copyFace(face) {
    return {
        x: face.x,
        y: face.y,
        width: face.width,
        height: face.height,
        isSubject: !!face.isSubject,
    };
}

function mergeFaceWeighted(a, wA, b, wB) {
    const w = wA + wB;
    if (w <= 0) {
        return copyFace(a);
    }
    return {
        x: (a.x * wA + b.x * wB) / w,
        y: (a.y * wA + b.y * wB) / w,
        width: (a.width * wA + b.width * wB) / w,
        height: (a.height * wA + b.height * wB) / w,
        isSubject: !!(a.isSubject || b.isSubject),
    };
}

/**
 * 跨抽检帧聚类同一张脸。
 * @param {FragmentSampleRow[]} samples
 * @param {typeof DEFAULT_FRAGMENT_CONSENSUS_OPTS} opts
 */
function clusterFacesAcrossSamples(samples, opts) {
    const sim = mergeSimilarityOpts(opts);
    /** @type {{ faceData: FaceData, supportFrames: number, weight: number }[]} */
    const clusters = [];

    for (const row of samples) {
        const faces = filterFacesArrayByMinEdge(row.facesData, opts.minFaceWidth, opts.minFaceHeight);
        /** @type {Set<number>} */
        const hitClusterIdx = new Set();

        for (const face of faces) {
            let matchedIdx = -1;
            for (let i = 0; i < clusters.length; i++) {
                if (facesSimilarForMerge(clusters[i].faceData, face, sim)) {
                    matchedIdx = i;
                    break;
                }
            }

            if (matchedIdx >= 0 && !hitClusterIdx.has(matchedIdx)) {
                const c = clusters[matchedIdx];
                c.faceData = mergeFaceWeighted(c.faceData, c.weight, face, 1);
                c.weight += 1;
                c.supportFrames += 1;
                hitClusterIdx.add(matchedIdx);
            } else if (matchedIdx < 0) {
                clusters.push({
                    faceData: copyFace(face),
                    supportFrames: 1,
                    weight: 1,
                });
            }
        }
    }

    return clusters;
}

/** @param {FaceData} face @param {{ faceData: FaceData }[]} clusters @param {object} sim */
function findClusterIndex(face, clusters, sim) {
    for (let i = 0; i < clusters.length; i++) {
        if (facesSimilarForMerge(clusters[i].faceData, face, sim)) {
            return i;
        }
    }
    return -1;
}

/** 单帧命中 cluster 集合 → 签名 */
function signatureForRow(row, clusters, opts) {
    const sim = mergeSimilarityOpts(opts);
    const faces = filterFacesArrayByMinEdge(row.facesData, opts.minFaceWidth, opts.minFaceHeight);
    /** @type {number[]} */
    const ids = [];
    for (const face of faces) {
        const idx = findClusterIndex(face, clusters, sim);
        if (idx >= 0 && !ids.includes(idx)) {
            ids.push(idx);
        }
    }
    ids.sort((a, b) => a - b);
    return ids.length ? ids.join(',') : '';
}

/**
 * 从镜头内抽检结果生成 canonical segments：取得票最多的人脸组合（帧签名投票）。
 * @param {FragmentSampleRow[]} samples
 * @param {Partial<typeof DEFAULT_FRAGMENT_CONSENSUS_OPTS>} [opts]
 * @returns {{ trackId: number, faceData: FaceData, frameCount: number }[]}
 */
export function buildFragmentConsensusSegments(samples, opts = {}) {
    const merged = { ...DEFAULT_FRAGMENT_CONSENSUS_OPTS, ...opts };
    if (!Array.isArray(samples) || samples.length === 0) {
        return [];
    }

    const clusters = clusterFacesAcrossSamples(samples, merged);
    if (clusters.length === 0) {
        return [];
    }

    /** @type {Map<string, { count: number, clusterIds: number[] }>} */
    const votes = new Map();

    for (const row of samples) {
        const sig = signatureForRow(row, clusters, merged);
        const clusterIds = sig ? sig.split(',').map(Number) : [];
        const prev = votes.get(sig);
        if (prev) {
            prev.count += 1;
        } else {
            votes.set(sig, { count: 1, clusterIds });
        }
    }

    /** @type {{ sig: string, count: number, clusterIds: number[] }[]} */
    const ranked = [...votes.entries()].map(([sig, v]) => ({
        sig,
        count: v.count,
        clusterIds: v.clusterIds,
    }));

    // 优先非空签名；并列时：票数 → 人脸数 → cluster 总 support
    ranked.sort((a, b) => {
        const aEmpty = a.sig === '' ? 1 : 0;
        const bEmpty = b.sig === '' ? 1 : 0;
        if (aEmpty !== bEmpty) return aEmpty - bEmpty;
        if (b.count !== a.count) return b.count - a.count;
        if (b.clusterIds.length !== a.clusterIds.length) return b.clusterIds.length - a.clusterIds.length;
        const sup = ids => ids.reduce((s, id) => s + clusters[id].supportFrames, 0);
        return sup(b.clusterIds) - sup(a.clusterIds);
    });

    const winner = ranked[0];
    if (!winner || winner.sig === '') {
        return [];
    }

    const winCount = winner.count;

    return winner.clusterIds
        .map((clusterId, i) => ({
            trackId: i,
            faceData: copyFace(clusters[clusterId].faceData),
            frameCount: Math.max(1, winCount),
        }))
        .sort((a, b) => {
            const ax = a.faceData.x + a.faceData.width * 0.5;
            const bx = b.faceData.x + b.faceData.width * 0.5;
            return ax !== bx ? ax - bx : a.trackId - b.trackId;
        })
        .map((seg, i) => ({ ...seg, trackId: i }));
}
